import React, { useEffect, useMemo, useState, useRef } from 'react';
import { generateClient } from 'aws-amplify/api';
import * as queries from '../graphql/queries';
import useOperationalStore, { PIPELINE, IDLE_MS, getTimeHealth, isIdle, computeSlaRisk } from '../platform/store/useOperationalStore';
import useShopSummary from '../platform/engine/useShopSummary';
import { moneyTotals, moneyState } from '../platform/engine/estimateMoney';
import FullscreenBtn from '../components/FullscreenBtn';
import './CommandView.css';

const TOTAL_BAYS = 7;
const CUSTOMER_UPDATE_INTERVAL_MS = 24 * 60 * 60 * 1000;
const client = generateClient();

function healthColor(s) {
  if (s >= 80) return 'var(--tt-green)';
  if (s >= 60) return 'var(--tt-gold)';
  return 'var(--tt-red)';
}

function fmtMoneyShort(n) {
  if (!n) return '$0';
  if (n >= 100_000) return `$${Math.round(n / 1000)}k`;
  if (n >= 10_000) return `$${(n / 1000).toFixed(1)}k`;
  return `$${Math.round(n).toLocaleString()}`;
}

// ─── KPI tile ─────────────────────────────────────────────────────────────────

function KpiTile({ label, value, sub, accent, warn, crit }) {
  return (
    <div className={`cmd-tile${crit ? ' cmd-tile--crit' : warn ? ' cmd-tile--warn' : ''}`}>
      <span className="cmd-tile-val" style={accent ? { color: accent } : undefined}>
        {value}
      </span>
      {sub && <span className="cmd-tile-sub">{sub}</span>}
      <span className="cmd-tile-label">{label}</span>
    </div>
  );
}

// ─── Pipeline row ─────────────────────────────────────────────────────────────

function fmtAge(ms) {
  if (!ms || ms <= 0) return '';
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  if (h >= 24) return `${Math.floor(h / 24)}d`;
  if (h > 0)   return `${h}h`;
  return `${m}m`;
}

function PipelineRow({ stage, count, total, stageTrucks }) {
  const pct = total ? (count / total) * 100 : 0;

  const now = Date.now();
  const avgMs = count > 0
    ? stageTrucks.reduce((s, t) => s + (now - (t.lastStatusChange || now)), 0) / count
    : 0;
  const threshold  = IDLE_MS[stage.statuses[0]];
  const ageRatio   = threshold ? avgMs / threshold : 0;
  const ageColor   = ageRatio >= 1 ? 'var(--tt-red)' : ageRatio >= 0.6 ? 'var(--tt-gold)' : '#475569';

  return (
    <div className="cmd-pipe-row">
      <span className="cmd-pipe-label">{stage.label}</span>
      <div className="cmd-pipe-bar-wrap">
        <div className="cmd-pipe-bar" style={{ width: `${pct}%` }} />
      </div>
      <span className="cmd-pipe-count">{count}</span>
      <span className="cmd-pipe-age" style={{ color: ageColor }}>
        {count > 0 ? fmtAge(avgMs) : ''}
      </span>
    </div>
  );
}

// ─── Alert list ───────────────────────────────────────────────────────────────

function AlertList({ title, trucks, emptyMsg, rowAccent }) {
  const setSelectedTruckId = useOperationalStore(s => s.setSelectedTruckId);
  return (
    <div className="cmd-alert-section">
      <div className="cmd-alert-header" style={rowAccent ? { borderLeftColor: rowAccent } : undefined}>
        <span className="cmd-alert-title">{title}</span>
        {trucks.length > 0 && (
          <span className="cmd-alert-badge" style={rowAccent ? { background: rowAccent } : undefined}>
            {trucks.length}
          </span>
        )}
      </div>
      {trucks.length === 0 ? (
        <div className="cmd-alert-empty">{emptyMsg}</div>
      ) : (
        <div className="cmd-alert-list">
          {trucks.map(t => (
            <div key={t.id} className="cmd-alert-row" onClick={() => setSelectedTruckId(t.id)}>
              <span className="cmd-alert-ro">{t.repairOrder}</span>
              <span className="cmd-alert-info">{t.truckNumber}{t.companyName ? ` — ${t.companyName}` : ''}</span>
              <span className="cmd-alert-status">{t.status}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── SLA breakdown ────────────────────────────────────────────────────────────

const SLA_LEVELS = [
  { key: 'CRITICAL', label: 'Critical', color: 'var(--tt-red)' },
  { key: 'HIGH',     label: 'High',     color: 'var(--tt-gold)' },
  { key: 'MEDIUM',   label: 'Medium',   color: 'var(--tt-gold)' },
  { key: 'LOW',      label: 'Low',      color: 'var(--tt-green)' },
];

function SlaBreakdown({ trucks }) {
  const setSelectedTruckId = useOperationalStore(s => s.setSelectedTruckId);
  const counts = {};
  SLA_LEVELS.forEach(l => { counts[l.key] = trucks.filter(t => computeSlaRisk(t) === l.key); });

  return (
    <div className="cmd-card cmd-card--sla">
      <div className="cmd-card-title">SLA Risk</div>
      <div className="cmd-sla-bars">
        {SLA_LEVELS.map(({ key, label, color }) => {
          const group = counts[key];
          const pct   = trucks.length ? (group.length / trucks.length) * 100 : 0;
          return (
            <div key={key} className="cmd-sla-row">
              <span className="cmd-sla-label" style={{ color }}>{label}</span>
              <div className="cmd-sla-bar-wrap">
                <div className="cmd-sla-bar" style={{ width: `${pct}%`, background: color }} />
              </div>
              <span className="cmd-sla-count">{group.length}</span>
            </div>
          );
        })}
      </div>
      {/* Top risk trucks */}
      {counts['CRITICAL'].length > 0 && (
        <div className="cmd-sla-crit-list">
          {counts['CRITICAL'].slice(0, 3).map(t => (
            <div key={t.id} className="cmd-sla-crit-row" onClick={() => setSelectedTruckId(t.id)}>
              <span className="cmd-sla-crit-ro">{t.repairOrder}</span>
              <span className="cmd-sla-crit-info">{t.truckNumber}{t.companyName ? ` — ${t.companyName}` : ''}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Tech utilization ─────────────────────────────────────────────────────────

const RING_R   = 20;
const RING_C   = 2 * Math.PI * RING_R;
const MAX_LOAD = 3;

function ringColor(count) {
  if (count === 0) return '#334155';
  const pct = count / MAX_LOAD;
  if (pct >= 1)    return 'var(--tt-red)';
  if (pct >= 0.67) return 'var(--tt-gold)';
  return 'var(--tt-green)';
}

function TechRing({ tech, count }) {
  const color   = ringColor(count);
  const fillPct = Math.min(1, count / MAX_LOAD);
  const offset  = RING_C * (1 - fillPct);

  return (
    <div className="cmd-tech-ring">
      <svg width="56" height="56" viewBox="0 0 56 56">
        <circle cx="28" cy="28" r={RING_R} fill="none" stroke="#e2e8f0" strokeWidth="5" />
        {count > 0 && (
          <circle
            cx="28" cy="28" r={RING_R} fill="none"
            stroke={color} strokeWidth="5"
            strokeDasharray={RING_C}
            strokeDashoffset={offset}
            strokeLinecap="round"
            transform="rotate(-90 28 28)"
            style={{ transition: 'stroke-dashoffset 0.4s ease' }}
          />
        )}
        <text x="28" y="33" textAnchor="middle" fontSize="14" fontWeight="800"
          fill={count > 0 ? color : '#475569'}
          fontFamily="ui-sans-serif, system-ui, sans-serif">
          {count}
        </text>
      </svg>
      <span className="cmd-tech-name">{tech.name?.split(' ')[0] ?? `#${tech.number}`}</span>
      <span className="cmd-tech-num">#{tech.number}</span>
    </div>
  );
}

function TechUtilization({ trucks, technicians }) {
  const active = technicians.filter(t => t.active !== false);
  if (!active.length) return null;

  const loads = active
    .map(tech => ({
      tech,
      count: trucks.filter(t =>
        (t.techNumbers || []).includes(tech.number) || t.techNumber === tech.number
      ).length,
    }))
    .sort((a, b) => b.count - a.count);

  const unassigned = trucks.filter(t =>
    !(t.techNumbers?.filter(Boolean).length) && !t.techNumber
  ).length;

  return (
    <div className="cmd-card cmd-card--techs">
      <div className="cmd-card-title">Tech Load</div>
      <div className="cmd-tech-rings">
        {loads.map(({ tech, count }) => (
          <TechRing key={tech.id} tech={tech} count={count} />
        ))}
      </div>
      {unassigned > 0 && (
        <div className="cmd-tech-unassigned">{unassigned} job{unassigned !== 1 ? 's' : ''} unassigned</div>
      )}
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

const PANEL_LABELS = ['Overview', 'Pipeline', 'Alerts', 'Team'];

export default function CommandView() {
  const trucks        = useOperationalStore(s => s.trucks);
  const technicians   = useOperationalStore(s => s.technicians);
  const getShopHealth = useOperationalStore(s => s.getShopHealth);
  const { summary, loading, error, refresh } = useShopSummary();

  const [panel, setPanel] = useState(0);
  const [customerUpdateEvents, setCustomerUpdateEvents] = useState([]);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);

  useEffect(() => {
    let cancelled = false;

    async function loadCustomerUpdateEvents() {
      if (!queries.listOperationEvents) return;
      try {
        const result = await client.graphql({
          query: queries.listOperationEvents,
          variables: {
            limit: 500,
            filter: { eventType: { eq: 'CUSTOMER_UPDATED' } },
          },
        });
        if (!cancelled) setCustomerUpdateEvents(result.data?.listOperationEvents?.items || []);
      } catch (err) {
        console.warn('Could not load customer update events:', err.message);
        if (!cancelled) setCustomerUpdateEvents([]);
      }
    }

    loadCustomerUpdateEvents();
    const timer = setInterval(loadCustomerUpdateEvents, 120000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  function onTouchStart(e) {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  }

  function onTouchEnd(e) {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = Math.abs(e.changedTouches[0].clientY - touchStartY.current);
    if (Math.abs(dx) > 55 && Math.abs(dx) > dy * 1.4) {
      if (dx < 0) setPanel(p => Math.min(p + 1, PANEL_LABELS.length - 1));
      else         setPanel(p => Math.max(p - 1, 0));
    }
    touchStartX.current = null;
  }

  const active       = trucks.filter(t => !['completed', 'voided'].includes(t.status));
  const fleetDown    = active.filter(t => t.isHardDown);
  const idleTrucks   = active.filter(isIdle);
  const overdue      = active.filter(t => getTimeHealth(t)?.status === 'red');
  const awaitingApproval = active.filter(t => t.status === 'approval');
  const partsPending     = active.filter(t => t.status === 'parts-hold');
  // Money spans ALL trucks — an unpaid invoice on a completed RO is still owed.
  const money            = moneyTotals(trucks);
  const unpaidCount      = trucks.filter(t => moneyState(t).stage === 'invoiced').length;
  const baysOccupied = new Set(active.filter(t => t.bayId).map(t => t.bayId)).size;
  const health       = getShopHealth();
  const customerUpdatedAtByWorkOrder = useMemo(() => {
    return customerUpdateEvents.reduce((map, event) => {
      if (!event.workOrderId) return map;
      map[event.workOrderId] = Math.max(map[event.workOrderId] || 0, event.timestamp || 0);
      return map;
    }, {});
  }, [customerUpdateEvents]);
  const customerUpdatesOverdue = active.filter(truck => {
    const last = customerUpdatedAtByWorkOrder[truck.id];
    if (!last) return true;
    const lastMs = last < 10_000_000_000 ? last * 1000 : last;
    return Date.now() - lastMs >= CUSTOMER_UPDATE_INTERVAL_MS;
  });

  const kpiRow = (
    <div className="cmd-kpi-row">
      <KpiTile label="Shop Health" value={health} sub="/100" accent={healthColor(health)} />
      <KpiTile label="Active Jobs"   value={active.length} />
      <KpiTile label="Hard Down"    value={fleetDown.length}        crit={fleetDown.length > 0} />
      <KpiTile label="Idle"          value={idleTrucks.length}       warn={idleTrucks.length > 0} />
      <KpiTile label="Overdue"       value={overdue.length}          warn={overdue.length > 0} crit={overdue.length > 3} />
      <KpiTile label="Cust Updates"  value={customerUpdatesOverdue.length} warn={customerUpdatesOverdue.length > 0} crit={customerUpdatesOverdue.length > 3} />
      <KpiTile label="Approval"      value={awaitingApproval.length} sub={money.sent > 0 ? fmtMoneyShort(money.sent) : undefined} warn={awaitingApproval.length > 0} />
      <KpiTile label="Unpaid"        value={unpaidCount} sub={money.invoiced > 0 ? fmtMoneyShort(money.invoiced) : undefined} warn={unpaidCount > 0} crit={money.overdue > 0} />
      <KpiTile label="Parts"         value={partsPending.length}     warn={partsPending.length > 0} />
      <KpiTile label="Bays Occupied" value={baysOccupied}            sub={`/ ${TOTAL_BAYS}`} />
    </div>
  );

  const sitrep = (error || loading || summary) ? (
    <div className="cmd-sitrep">
      {error === 'no-key' ? (
        <span className="cmd-sitrep-error">AI summary unavailable - add ANTHROPIC_API_KEY to the server environment</span>
      ) : error === 'no-credits' ? (
        <span className="cmd-sitrep-error">AI summary unavailable — the Anthropic account is out of credits (console.anthropic.com → Billing)</span>
      ) : error === 'fetch-failed' ? (
        <span className="cmd-sitrep-error">AI summary unavailable — check console for details</span>
      ) : loading ? (
        <span className="cmd-sitrep-loading">Analyzing operations…</span>
      ) : summary ? (
        <>
          <span className="cmd-sitrep-icon">◈</span>
          <span className="cmd-sitrep-text">{summary}</span>
          <button className="cmd-sitrep-refresh" onClick={refresh} title="Refresh summary">↻</button>
        </>
      ) : null}
    </div>
  ) : null;

  const pipelineCard = (
    <div className="cmd-card">
      <div className="cmd-card-title">Pipeline Breakdown</div>
      <div className="cmd-pipeline">
        {PIPELINE.map(stage => {
          const stageTrucks = active.filter(t => stage.statuses.includes(t.status));
          return (
            <PipelineRow key={stage.id} stage={stage} count={stageTrucks.length} total={active.length} stageTrucks={stageTrucks} />
          );
        })}
      </div>
      <div className="cmd-pipeline-total">
        <span>{active.length} active jobs across {PIPELINE.length} stages</span>
      </div>
    </div>
  );

  const alertLists = (
    <div className="cmd-alerts">
      <AlertList title="Hard Down"        trucks={fleetDown}          emptyMsg="No hard-down units"    rowAccent="var(--tt-red)" />
      <AlertList title="Idle Jobs"        trucks={idleTrucks}         emptyMsg="All jobs moving"       rowAccent="var(--tt-gold)" />
      <AlertList title="Overdue"          trucks={overdue}            emptyMsg="No overdue jobs"       rowAccent="var(--tt-gold)" />
      <AlertList title="Customer Updates" trucks={customerUpdatesOverdue} emptyMsg="Customer updates current" rowAccent="var(--tt-red)" />
      <AlertList title="Awaiting Approval" trucks={awaitingApproval}  emptyMsg="No pending approvals"  rowAccent="var(--tt-gold)" />
      <AlertList title="Parts"            trucks={partsPending}       emptyMsg="No parts holds"        rowAccent="#3b82f6" />
    </div>
  );

  return (
    <div className="cmd-root">

      {/* ─── Desktop layout ─── */}
      <div className="cmd-desktop-layout">

      {/* ── Header ── */}
      <div className="vtb">
        <div className="vtb-left">
          <span className="cmd-live">● LIVE</span>
        </div>
        <div className="vtb-right"><FullscreenBtn /></div>
      </div>

      {sitrep}
      {kpiRow}

      {/* ── Body ── */}
      <div className="cmd-body">
        <div className="cmd-body-top">
          {pipelineCard}
          <SlaBreakdown trucks={active} />
          {alertLists}
        </div>
        <TechUtilization trucks={active} technicians={technicians} />
      </div>

      </div>{/* end cmd-desktop-layout */}

      {/* ─── Mobile layout: 4 swipeable panels ─── */}
      <div
        className="cmd-mobile-layout"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <div
          className="cmd-mobile-track"
          style={{ transform: `translateX(calc(-${panel} * 100vw))` }}
        >
          {/* Panel 0: Overview */}
          <div className="cmd-mobile-panel">
            {sitrep}
            {kpiRow}
          </div>

          {/* Panel 1: Pipeline */}
          <div className="cmd-mobile-panel">
            {pipelineCard}
            <SlaBreakdown trucks={active} />
          </div>

          {/* Panel 2: Alerts */}
          <div className="cmd-mobile-panel">
            {alertLists}
          </div>

          {/* Panel 3: Team */}
          <div className="cmd-mobile-panel">
            <TechUtilization trucks={active} technicians={technicians} />
          </div>
        </div>

        {/* Panel dot nav */}
        <div className="cmd-mobile-dots">
          {PANEL_LABELS.map((label, i) => (
            <button
              key={label}
              className={`cmd-dot${panel === i ? ' cmd-dot--active' : ''}`}
              onClick={() => setPanel(i)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

    </div>
  );
}
